import type { MediaMetricLog, Metric, MetricLog } from '../types';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function randomInt(min: number, max: number): number {
  return Math.floor(min + Math.random() * (max - min + 1));
}

function dayTimestamp(daysAgo: number, hour = 12): number {
  const date = new Date();
  date.setHours(hour, randomInt(0, 59), randomInt(0, 59), 0);
  date.setDate(date.getDate() - daysAgo);
  return date.getTime();
}

function generateNumberLogs(metric: Metric, days: number): MetricLog[] {
  const logs: MetricLog[] = [];

  for (let daysAgo = 0; daysAgo < days; daysAgo++) {
    const mean = 180;
    const spread = (Math.random() - 0.5) * 10;
    const value = Math.round(Math.max(140, Math.min(220, mean + spread)));

    logs.push({
      id: crypto.randomUUID(),
      metricId: metric.id,
      timestamp: dayTimestamp(daysAgo, 7),
      value,
    });
  }

  return logs;
}

function generateCounterLogs(metric: Metric, days: number): MetricLog[] {
  const logs: MetricLog[] = [];

  for (let daysAgo = 0; daysAgo < days; daysAgo++) {
    const entriesToday = randomInt(1, 5);

    for (let entry = 0; entry < entriesToday; entry++) {
      logs.push({
        id: crypto.randomUUID(),
        metricId: metric.id,
        timestamp: dayTimestamp(daysAgo, 8 + entry * 2),
        value: 1,
      });
    }
  }

  return logs;
}

function generateScaleLogs(metric: Metric, days: number): MetricLog[] {
  const logs: MetricLog[] = [];

  for (let daysAgo = 0; daysAgo < days; daysAgo++) {
    logs.push({
      id: crypto.randomUUID(),
      metricId: metric.id,
      timestamp: dayTimestamp(daysAgo, 20),
      value: randomInt(1, 5),
    });
  }

  return logs;
}

function generateMediaTextLogs(metric: Metric, days: number): MediaMetricLog[] {
  const logs: MediaMetricLog[] = [];

  for (let daysAgo = 0; daysAgo < days; daysAgo++) {
    logs.push({
      id: crypto.randomUUID(),
      metricId: metric.id,
      timestamp: dayTimestamp(daysAgo, 20),
      textValue: `apples x${daysAgo} sauced`,
    });
  }

  return logs;
}

/**
 * Generates realistic log entries for the past N days (default: 30).
 * Media metrics are skipped — no MetricLog entries are produced for them.
 */
export function generateSeedLogs(metrics: Metric[], days = 30): MetricLog[] {
  const logs: MetricLog[] = [];

  for (const metric of metrics) {
    switch (metric.type) {
      case 'number':
        logs.push(...generateNumberLogs(metric, days));
        break;
      case 'counter':
        logs.push(...generateCounterLogs(metric, days));
        break;
      case 'scale':
        logs.push(...generateScaleLogs(metric, days));
        break;
      case 'media':
        break;
    }
  }

  return logs;
}

export function generateSeedMediaLogs(
  metrics: Metric[],
  days = 30
): MediaMetricLog[] {
  const logs: MediaMetricLog[] = [];

  for (const metric of metrics) {
    if (metric.type === 'media') {
      logs.push(...generateMediaTextLogs(metric, days));
    }
  }

  return logs;
}

export function getSeedLogCountBounds(
  metric: Metric,
  days: number
): { min: number; max: number } {
  switch (metric.type) {
    case 'number':
    case 'scale':
      return { min: days, max: days };
    case 'counter':
      return { min: days, max: days * 5 };
    case 'media':
      return { min: 0, max: 0 };
  }
}

export { MS_PER_DAY };
